import React, { useEffect, useState, type HTMLAttributes } from "react";
import { useStore } from "@nanostores/react";
import clsx from "clsx";
import { Chip } from "@nextui-org/chip";

import { parseResources, type ParseResource, type Posts } from "@module/blog";
import { excludes, type } from "@module/blog/store";
import { useTranslated } from "@i18next/client/utils";

// widget
import BlogsCard from "@react/widget/BlogsCard";
import Img from "@react/widget/Img";
import { H2 } from "@react/widget/Headline";
import Button from "@react/widget/Button";
import Section from "@react/structure/Section";

interface Props extends HTMLAttributes<'div'> {
  currentSite: Page.Site;
  init: Array<Posts>;
  label: {
    readTheBlog: string; // "Read the Blog"
  };
}

var first = true;

const LatestCards: React.FC<Props> = ({ currentSite, init, label}) => {
  const { tr } = useTranslated(currentSite.locale);
  const $type = useStore(type);
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState(parseResources(init));
  const [latestFirst = undefined, ...latestOthers] = entries;
  useEffect(() => {
    if (!first) {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("site", currentSite.handle);
      //types[$type ?? 0]?.section?.forEach(section => params.append("section", section));
      fetch(`/api/blog/feature.json?${params.toString()}`)
        .then(res => res.json())
        .then((res: Array<Posts>) => {
          setEntries(parseResources(res));
          excludes.set(res.map(e => `${e.id}`));
          setLoading(false);
          return res;
        })
        .catch(reason => {
          console.error(reason);
          setLoading(false);
          return undefined;
        });
    } else {
      excludes.set(entries.map(e => `${e.id}`));
    }
    first = false;
  }, [$type]);

  if (!latestFirst)
    return null;

  return (
    <>
      <Section>
        <BlogsCard key="latest" isLoaded={!loading} rounded="none"
          className="max-w-sm md:max-w-full mx-auto w-full [&:hover_img]:scale-110 overflow-hidden md:flex-row-reverse gap-8 md:gap-4 md:[&>div:first-child]:w-7/12 [&>div:last-child]:border-0 [&>div:last-child]:p-0 md:[&>div:last-child]:w-5/12 md:[&>div:last-child]:min-h-full hover:!shadow-none !items-center"
          blogFeaturedImage={(
            <a href={tr(latestFirst.uri)} className={clsx("block absolute inset-0 rounded-lg overflow-hidden")}>
              {latestFirst && <Img className={clsx("w-full h-full object-cover transition duration-500 absolute inset-0")}
                data={latestFirst.blogFeaturedImage}
                width={690}
                height={387}
                fit={"cover"}
              />}
            </a>
          )}
          topics={latestFirst.topics?.map(t => (
            <a key={t.slug} href={tr(`/blog/category/${t.slug}`)}>
              <Chip size="sm" variant="flat" classNames={{ base: `rounded-md border-1 transition hover:grayscale` }} style={{ borderColor: t.color, color: t.color, backgroundColor: `${t.color}0f` }}>
                {t.title}
              </Chip>
            </a>
          ))}
          summary={(
            <a href={tr(latestFirst.uri)}>
              <H2 size="2xl" className="hover:text-primary transition duration-500 line-clamp-3" title={latestFirst?.title}>{latestFirst?.title}</H2>
            </a>
          )}
          cta={(
            <Button
              className="mt-4"
              size="lg"
              style="link"
              href={tr(latestFirst.uri)}
              icon="arrow-right-long"
            >{label.readTheBlog}</Button>
          )}
          tags={latestFirst.tags?.slice(0, 3).map(t => (
            <a key={t.slug} href={tr(t.uri)}>
              <Chip key={t.slug} size="sm" variant="faded" classNames={{ base: `rounded-md bg-transparent capitalize hover:bg-gray-60 transition duration-500` }}>{t.title}</Chip>
            </a>
          ))}
        />
        {!!latestOthers?.length && (
          <div className="grid md:grid-cols-2 gap-8 border-t pt-12 mt-12 md:mt-24 md:pt-24">
            {
              latestOthers?.map((entry, i) => {
                return (
                  <BlogsCard isLoaded={!loading} key={`blogs-${i}`} href={tr(entry.uri)} rounded="none" blogFeaturedImage={(
                    <a href={tr(entry.uri)} className={clsx("block absolute inset-0 rounded-lg overflow-hidden")}>
                      {entry && (
                        <Img className={clsx("w-full h-full object-cover transition duration-500 absolute inset-0")}
                          src={entry.blogFeaturedImage}
                          width={584}
                          height={327}
                          fit={"cover"}
                        />
                      )}
                    </a>
                  )} topics={entry.topics?.map(t => (
                    <a key={t.slug} href={tr(`/blog/category/${t.slug}`)}>
                      <Chip
                        size="sm"
                        variant="flat"
                        classNames={{
                          base: `rounded-md border-1 transition hover:grayscale`,
                        }}
                        style={{
                          borderColor: t.color,
                          color: t.color,
                          backgroundColor: `${t.color}0f`,
                        }}
                      >
                        {t.title}
                      </Chip>
                    </a>
                  ))}
                    summary={(
                      <a href={tr(entry.uri)}>
                        <H2
                          size="lg"
                          className="hover:text-primary transition duration-500 line-clamp-2"
                          title={entry?.title}
                        >{entry?.title}</H2>
                      </a>
                    )}
                    cta={(
                      <Button
                        className="mt-4"
                        style="link"
                        size="lg"
                        href={tr(entry.uri)}
                        icon="arrow-right-long"
                      >{label.readTheBlog}
                      </Button>
                    )}
                    tags={entry.tags?.slice(0, 3).map(t => (
                      <a key={t.slug} href={tr(t.uri)}>
                        <Chip
                          key={t.slug}
                          size="sm"
                          variant="faded"
                          classNames={{
                            base: `rounded-md bg-transparent capitalize hover:bg-gray-60 transition duration-500`,
                          }}
                        >
                          {t.title}
                        </Chip>
                      </a>
                    ))} className="max-w-sm md:max-w-full mx-auto w-full [&:hover_img]:scale-110 overflow-hidden hover:shadow-none [&>div]:border-0" />
                )
              })
            }
          </div>
        )}
      </Section>
      <Section py="0"><hr /></Section>
    </>
  );
}

export default LatestCards;